import { useEffect, useState } from 'react';
import { api, type Layer } from '../lib/api';
import { readPaintConfig, buildPaintJson, geomKind, type PaintConfig } from '../lib/paint';

type Props = {
  layer: Layer;
  onChange: () => void;
  onDelete: () => void;
};

export function LayerSettings({ layer, onChange, onDelete }: Props) {
  const [name, setName]               = useState(layer.name);
  const [cfg, setCfg]                 = useState<PaintConfig>(() => readPaintConfig(layer));
  const [attrs, setAttrs]             = useState<string[]>(layer.attributes);
  const [popup, setPopup]             = useState(layer.popupTemplate ?? '');
  const [minZoom, setMinZoom]         = useState(layer.minZoom);
  const [maxZoom, setMaxZoom]         = useState(layer.maxZoom);
  const [columns, setColumns]         = useState<{ name: string; type: string }[]>([]);
  const [saving, setSaving]           = useState(false);
  const [dirty, setDirty]             = useState(false);

  useEffect(() => {
    api.listColumns(layer.sourceSchema, layer.sourceTable).then(r => setColumns(r.columns));
  }, [layer.sourceSchema, layer.sourceTable]);

  const kind = geomKind(layer.geometryType);
  const numericCols = columns.filter(c => /^(integer|bigint|smallint|numeric|double|real)/i.test(c.type));
  const stringCols  = columns.filter(c => /^(text|character|varchar|character varying)/i.test(c.type));

  function patch<K extends keyof PaintConfig>(k: K, v: PaintConfig[K]) {
    setCfg(c => ({ ...c, [k]: v })); setDirty(true);
  }

  function toggleAttr(name: string) {
    setAttrs(a => a.includes(name) ? a.filter(x => x !== name) : [...a, name]);
    setDirty(true);
  }

  function insertToken(col: string) {
    setPopup(p => p + (p && !p.endsWith(' ') && !p.endsWith('>') ? ' ' : '') + `{{${col}}}`);
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      const paint = buildPaintJson(layer, cfg);
      await api.updateLayer(layer.id, {
        name, paint, attributes: attrs,
        popupTemplate: popup || null, minZoom, maxZoom,
      });
      setDirty(false); onChange();
    } finally { setSaving(false); }
  }

  return (
    <div style={{ fontSize: 13 }}>
      <header style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Capa: {layer.name}</h3>
        <code style={{ color: '#64748b', fontSize: 11 }}>{layer.sourceSchema}.{layer.sourceTable} · {layer.geometryType}</code>
      </header>

      <Field label="Nombre">
        <input value={name} onChange={e => { setName(e.target.value); setDirty(true); }} style={inp} />
      </Field>

      <hr style={hr} />
      <h4 style={h4}>Estilo</h4>
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {(['solid','categorical','gradient'] as const).map(m => (
          <button key={m} onClick={() => patch('mode', m)}
            style={{
              flex: 1, padding: '6px 4px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
              background: cfg.mode === m ? '#0ea5e9' : 'white',
              color:      cfg.mode === m ? 'white'   : '#0f172a',
              border: `1px solid ${cfg.mode === m ? '#0ea5e9' : '#cbd5e1'}`,
            }}>
            {m === 'solid' ? 'Sólido' : m === 'categorical' ? 'Categorías' : 'Gradiente'}
          </button>
        ))}
      </div>

      {cfg.mode === 'solid' && (
        <Field label={kind === 'fill' ? 'Color de relleno' : kind === 'line' ? 'Color de línea' : 'Color del punto'}>
          <ColorRow value={cfg.color ?? '#0ea5e9'} onChange={v => patch('color', v)} />
        </Field>
      )}

      {cfg.mode === 'categorical' && (
        <>
          <Field label="Columna (string)">
            <select value={cfg.byColumn ?? ''} onChange={e => patch('byColumn', e.target.value)} style={inp}>
              <option value="">— elegir —</option>
              {stringCols.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
            </select>
          </Field>
          <p style={{ fontSize: 11, color: '#475569', margin: '0 0 4px' }}>Categorías:</p>
          {(cfg.categories ?? []).map((cat, i) => (
            <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
              <input value={cat.value} onChange={e => {
                const next = [...(cfg.categories ?? [])]; next[i] = { ...cat, value: e.target.value };
                patch('categories', next);
              }} placeholder="valor" style={{ ...inp, flex: 1 }} />
              <input type="color" value={cat.color} onChange={e => {
                const next = [...(cfg.categories ?? [])]; next[i] = { ...cat, color: e.target.value };
                patch('categories', next);
              }} style={{ width: 32, height: 28, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }} />
              <button onClick={() => {
                const next = [...(cfg.categories ?? [])]; next.splice(i, 1); patch('categories', next);
              }} style={{ width: 22, height: 22, padding: 0, border: '1px solid #fecaca', background: 'white', color: '#dc2626', borderRadius: 4, cursor: 'pointer' }}>×</button>
            </div>
          ))}
          <button onClick={() => patch('categories', [...(cfg.categories ?? []), { value: '', color: '#0ea5e9' }])}
            style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px dashed #cbd5e1', background: 'white', cursor: 'pointer', marginBottom: 8 }}>
            + Categoría
          </button>
          <Field label="Color por defecto">
            <ColorRow value={cfg.defaultColor ?? '#94a3b8'} onChange={v => patch('defaultColor', v)} />
          </Field>
        </>
      )}

      {cfg.mode === 'gradient' && (
        <>
          <Field label="Columna numérica">
            <select value={cfg.byNumeric ?? ''} onChange={e => patch('byNumeric', e.target.value)} style={inp}>
              <option value="">— elegir —</option>
              {numericCols.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Mínimo (valor)">
              <input type="number" value={cfg.rampMin ?? 0} onChange={e => patch('rampMin', +e.target.value)} style={inp} />
            </Field>
            <Field label="Máximo (valor)">
              <input type="number" value={cfg.rampMax ?? 1} onChange={e => patch('rampMax', +e.target.value)} style={inp} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Color min">
              <ColorRow value={cfg.rampColors?.[0] ?? '#fef3c7'} onChange={v => patch('rampColors', [v, cfg.rampColors?.[1] ?? '#dc2626'])} />
            </Field>
            <Field label="Color max">
              <ColorRow value={cfg.rampColors?.[1] ?? '#dc2626'} onChange={v => patch('rampColors', [cfg.rampColors?.[0] ?? '#fef3c7', v])} />
            </Field>
          </div>
        </>
      )}

      <Field label={`Opacidad (${Math.round((cfg.opacity ?? 0.5) * 100)}%)`}>
        <input type="range" min={0} max={1} step={0.05} value={cfg.opacity ?? 0.5}
          onChange={e => patch('opacity', +e.target.value)} style={{ width: '100%' }} />
      </Field>
      {kind === 'fill' && (
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginBottom: 10, padding: '6px 8px', background: '#f1f5f9', borderRadius: 4 }}>
          <input type="checkbox" checked={(cfg.opacity ?? 0.5) === 0}
            onChange={e => patch('opacity', e.target.checked ? 0 : 0.5)} />
          <span>Solo borde (capa base — sirve para drill-down al click)</span>
        </label>
      )}

      {kind === 'fill' && (
        <Field label="Color de borde">
          <ColorRow value={cfg.outlineColor ?? '#0c4a6e'} onChange={v => patch('outlineColor', v)} />
        </Field>
      )}
      {kind === 'line' && (
        <Field label={`Grosor (${cfg.width ?? 2}px)`}>
          <input type="range" min={0.5} max={10} step={0.5} value={cfg.width ?? 2}
            onChange={e => patch('width', +e.target.value)} style={{ width: '100%' }} />
        </Field>
      )}
      {kind === 'circle' && (
        <>
          <Field label={`Radio (${cfg.radius ?? 5}px)`}>
            <input type="range" min={1} max={20} step={1} value={cfg.radius ?? 5}
              onChange={e => patch('radius', +e.target.value)} style={{ width: '100%' }} />
          </Field>
          <Field label="Color de borde">
            <ColorRow value={cfg.outlineColor ?? '#0c4a6e'} onChange={v => patch('outlineColor', v)} />
          </Field>
          <Field label={`Grosor de borde (${cfg.width ?? 1}px)`}>
            <input type="range" min={0} max={5} step={0.5} value={cfg.width ?? 1}
              onChange={e => patch('width', +e.target.value)} style={{ width: '100%' }} />
          </Field>
        </>
      )}

      <hr style={hr} />
      <h4 style={h4}>Visibilidad por zoom</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label={`Min (z${minZoom})`}>
          <input type="range" min={0} max={22} value={minZoom} onChange={e => { setMinZoom(+e.target.value); setDirty(true); }} style={{ width: '100%' }} />
        </Field>
        <Field label={`Max (z${maxZoom})`}>
          <input type="range" min={0} max={22} value={maxZoom} onChange={e => { setMaxZoom(+e.target.value); setDirty(true); }} style={{ width: '100%' }} />
        </Field>
      </div>

      <hr style={hr} />
      <h4 style={h4}>Atributos en MVT y popup</h4>
      <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 8px' }}>
        Las columnas seleccionadas se incluyen en el tile y quedan disponibles para el popup.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {columns.map(c => (
          <button key={c.name} onClick={() => toggleAttr(c.name)}
            style={{
              padding: '4px 8px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
              background: attrs.includes(c.name) ? '#0ea5e9' : 'white',
              color: attrs.includes(c.name) ? 'white' : '#0f172a',
              border: `1px solid ${attrs.includes(c.name) ? '#0ea5e9' : '#cbd5e1'}`,
            }}>
            {c.name} <span style={{ opacity: 0.6, fontSize: 10 }}>{c.type}</span>
          </button>
        ))}
      </div>

      <hr style={hr} />
      <h4 style={h4}>Popup template</h4>
      <p style={{ color: '#64748b', fontSize: 12, margin: '0 0 4px' }}>
        Click en una columna para insertar <code>&#123;&#123;col&#125;&#125;</code>. HTML básico permitido.
      </p>
      <textarea value={popup} onChange={e => { setPopup(e.target.value); setDirty(true); }}
        rows={4} placeholder="<b>{{nombre}}</b><br/>Electores: {{electores}}"
        style={{ ...inp, fontFamily: 'ui-monospace, SF Mono, monospace', fontSize: 12 }} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
        {attrs.map(a => (
          <button key={a} onClick={() => insertToken(a)} style={{ padding: '2px 6px', fontSize: 11, borderRadius: 3, cursor: 'pointer', border: '1px solid #cbd5e1', background: '#f1f5f9' }}>
            +{a}
          </button>
        ))}
        {attrs.length === 0 && <span style={{ color: '#94a3b8', fontSize: 11 }}>(elegí atributos arriba)</span>}
      </div>

      <hr style={hr} />
      <div style={{ display: 'flex', gap: 8, position: 'sticky', bottom: 0, background: 'white', paddingTop: 8 }}>
        <button onClick={save} disabled={!dirty || saving}
          style={{ flex: 1, padding: 10, background: dirty ? '#0ea5e9' : '#cbd5e1', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: dirty ? 'pointer' : 'not-allowed' }}>
          {saving ? 'Guardando…' : dirty ? 'Guardar' : 'Sin cambios'}
        </button>
        <button onClick={() => { if (confirm(`Eliminar capa "${layer.name}"?`)) onDelete(); }}
          style={{ padding: 10, background: 'transparent', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 6, cursor: 'pointer' }}>
          Eliminar
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <span style={{ display: 'block', fontSize: 11, color: '#475569', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

function ColorRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input type="color" value={value} onChange={e => onChange(e.target.value)} style={{ width: 36, height: 28, padding: 0, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }} />
      <input value={value} onChange={e => onChange(e.target.value)} style={{ ...inp, fontFamily: 'ui-monospace, SF Mono, monospace', fontSize: 12 }} />
    </div>
  );
}

const inp: React.CSSProperties = { width: '100%', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 4, boxSizing: 'border-box' };
const hr: React.CSSProperties = { border: 'none', borderTop: '1px solid #e2e8f0', margin: '14px 0' };
const h4: React.CSSProperties = { fontSize: 12, textTransform: 'uppercase', color: '#64748b', margin: '0 0 8px' };
